import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { UserData } from '../../models/storage/user-data';

interface StoredEnvelope {
  value: UserData;
  e?: number;
}

const AUTH_STORAGE_KEYS = ['storedData', 'isDeleted'];
const SESSION_FALLBACK_MS = 12 * 60 * 60 * 1000;

@Injectable({
  providedIn: 'root'
})
export class StorageService {

  private userData: UserData | null = null;

  constructor() { }

  setUserData(data: UserData): void {
    this.userData = data;
    this.setWithExpiry('storedData', this.userData);
  }

  /**
   * Returns the stored user without wiping session data when the access token expires.
   */
  getUserData(): UserData | null {
    const envelope = this.readEnvelope();
    return envelope?.value ?? null;
  }

  readEnvelope(): StoredEnvelope | null {
    const itemStr = localStorage.getItem('storedData');
    if (!itemStr) {
      return null;
    }
    try {
      const item = JSON.parse(itemStr);
      if (!item || typeof item !== 'object') {
        localStorage.removeItem('storedData');
        return null;
      }
      if (item.value) {
        return item as StoredEnvelope;
      }
      if (item.isLoggedIn !== undefined || item.token !== undefined) {
        return { value: item as UserData };
      }
      return null;
    } catch {
      localStorage.removeItem('storedData');
      return null;
    }
  }

  hasStoredSession(): boolean {
    const user = this.getUserData();
    return !!(user && user.isLoggedIn === true);
  }

  isAuthenticated(): boolean {
    const user = this.getUserData();
    return !!(user && user.isLoggedIn === true && this.isTokenCurrent(user.token));
  }

  hasValidCredentials(user: UserData | null | undefined, envelope?: StoredEnvelope | null): boolean {
    if (!user?.isLoggedIn || !user.token) {
      return false;
    }
    return this.isTokenCurrent(user.token);
  }

  isTokenCurrent(token: string | null | undefined, bufferMs = 30000): boolean {
    const expiry = this.tokenExpiryMs(token);
    return expiry !== null && (Date.now() + bufferMs) < expiry;
  }

  updateToken(token: string): void {
    const current = this.getUserData();
    if (current) {
      current.token = token;
      this.setUserData(current);
    }
  }

  clearUserData(): void {
    this.clearAuthSession();
  }

  clearAuthSession(): void {
    this.userData = null;
    AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
    const pastDate = new Date(0).toUTCString();
    document.cookie = `myObjectCookie=; domain=${environment.domain}; path=/; expires=${pastDate}`;
    document.cookie = `myObjectCookie=; path=/; expires=${pastDate}`;
  }

  setWithExpiry(key: string, value: any) {
    const item: StoredEnvelope = {
      value: value,
      e: Date.now() + (30 * 24 * 60 * 60 * 1000), // 30-day session lifetime envelope
    };
    localStorage.setItem(key, JSON.stringify(item));
  }

  private tokenExpiryMs(token: string | null | undefined): number | null {
    if (!token || token.split('.').length !== 3) {
      return null;
    }
    try {
      const payload = JSON.parse(this.decodeBase64Url(token.split('.')[1]));
      return typeof payload?.exp === 'number' ? payload.exp * 1000 : null;
    } catch {
      return null;
    }
  }

  private decodeBase64Url(value: string): string {
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    return decodeURIComponent(Array.prototype.map.call(atob(padded), (char: string) => {
      return '%' + ('00' + char.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
  }
}
